/**
 * rankingService.js - Serviço de Rankings com Cache Híbrido
 * 
 * Sistema de cache híbrido:
 * - RANKING GERAL: Atualiza a cada 30-60 min (cache Supabase)
 * - TOP 10: Atualiza a cada 30-50 min (cache Supabase)
 * - TOP 3 GLOBAL: Atualiza a cada 30-40 min (cache Supabase)
 * - TOP 3 DESAFIO: Atualiza a cada 30-40 min (cache Supabase)
 * - SUA POSIÇÃO: Tempo real (calculado localmente)
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../lib/supabase';
import { captureException } from './telemetry';

// Chaves de cache local
const CACHE_KEYS = {
    GLOBAL_RANKING: '@ranking_global',
    GLOBAL_TOP10: '@ranking_top10',
    GLOBAL_TOP3: '@ranking_top3',
    CHALLENGE_RANKINGS: '@ranking_challenges',
    USER_POSITION: '@user_position',
    LAST_FETCH: '@ranking_last_fetch',
};

// Intervalos de cache em milissegundos
const CACHE_INTERVALS = {
    GLOBAL_RANKING: 45 * 60 * 1000,    // 45 minutos
    GLOBAL_TOP10: 40 * 60 * 1000,      // 40 minutos
    GLOBAL_TOP3: 35 * 60 * 1000,       // 35 minutos
    CHALLENGE_TOP3: 35 * 60 * 1000,    // 35 minutos
    USER_POSITION: 0,                   // Tempo real
};

/**
 * Verifica se o cache local está expirado
 */
const isCacheExpired = async (cacheKey, interval) => {
    try {
        const lastFetchData = await AsyncStorage.getItem(CACHE_KEYS.LAST_FETCH);
        if (!lastFetchData) return true;

        const lastFetch = JSON.parse(lastFetchData);
        const lastTime = lastFetch[cacheKey] || 0;

        return Date.now() - lastTime > interval;
    } catch (error) {

        return true;
    }
};

/**
 * Atualiza timestamp do cache local
 */
const updateCacheTimestamp = async (cacheKey) => {
    try {
        const lastFetchData = await AsyncStorage.getItem(CACHE_KEYS.LAST_FETCH);
        const lastFetch = lastFetchData ? JSON.parse(lastFetchData) : {};
        lastFetch[cacheKey] = Date.now();
        await AsyncStorage.setItem(CACHE_KEYS.LAST_FETCH, JSON.stringify(lastFetch));
    } catch (error) {

    }
};

/**
 * Busca ranking do cache do Supabase
 */
const fetchFromSupabaseCache = async (cacheType, challengeId = null) => {
    // DESABILITADO: Tabela ranking_cache não existe no banco de dados
    // Retorna null para usar fallback local
    return null;

    /* CÓDIGO ORIGINAL COMENTADO - tabela não existe
    try {
        let query = supabase
            .from('ranking_cache')
            .select('data, expires_at, created_at')
            .eq('cache_type', cacheType);

        if (challengeId) {
            query = query.eq('challenge_id', challengeId);
        } else {
            query = query.is('challenge_id', null);
        }

        const { data, error } = await query.single();

        if (error) {

            return null;
        }

        // Verificar se cache expirou
        const expiresAt = new Date(data.expires_at);
        if (expiresAt < new Date()) {

            return null;
        }

        return {
            data: data.data,
            cachedAt: data.created_at,
            expiresAt: data.expires_at,
        };
    } catch (error) {

        return null;
    }
    */ // FIM DO CÓDIGO COMENTADO
};

// ============================================
// RANKING GLOBAL
// ============================================

/**
 * Busca ranking global completo (Top 100)
 * Cache: 45 minutos
 * ADAPTADO para schema existente do Supabase
 */
export const getGlobalRanking = async (forceRefresh = false) => {
    const cacheKey = 'global_ranking';

    // Verificar cache local primeiro
    if (!forceRefresh) {
        const expired = await isCacheExpired(cacheKey, CACHE_INTERVALS.GLOBAL_RANKING);
        if (!expired) {
            const cached = await AsyncStorage.getItem(CACHE_KEYS.GLOBAL_RANKING);
            if (cached) {
                const parsed = JSON.parse(cached);
                // Ignora cache vazio (ex.: envenenado por uma chamada anon/RLS sem
                // sessão que retornou 0 linhas) e força nova busca no banco.
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed;
                }
            }
        }
    }

    // Buscar do Supabase - usando colunas EXISTENTES
    try {
        const { data, error } = await supabase
            .from('profiles')
            .select('id, username, nome, foto_url, avatar_seed, nivel, xp_total, streak_atual, questoes_respondidas, questoes_corretas')
            .order('xp_total', { ascending: false })
            .limit(50); // Otimizado: 50 usuários é suficiente para exibição

        if (error) {
            captureException(error, { tags: { area: 'getGlobalRanking' } });
            return [];
        }

        // Formatar dados para formato compatível com o app
        const formattedData = data.map((item, index) => ({
            id: item.id,
            username: item.username || item.nome || 'user',
            displayName: item.nome || 'Usuário',
            display_name: item.nome || 'Usuário',
            name: item.nome || 'Usuário',
            avatar: getAvatarEmoji(index), // Fallback para emoji
            avatar_emoji: getAvatarEmoji(index),
            avatar_seed: item.avatar_seed || null,
            foto_url: item.foto_url || null,
            avatarBg: getAvatarColor(index),
            avatar_bg: getAvatarColor(index),
            avatarUrl: item.foto_url,
            isVerified: item.xp_total > 5000,
            isPremium: item.xp_total > 10000,
            isOnline: Math.random() > 0.5, // Simular status online
            xp: item.xp_total || 0,
            total_xp: item.xp_total || 0,
            level: item.nivel || 1,
            score: item.xp_total || 0,
            badge: index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}º`,
            accuracy: item.questoes_respondidas > 0
                ? Math.round((item.questoes_corretas / item.questoes_respondidas) * 100)
                : 0,
            accuracy_rate: item.questoes_respondidas > 0
                ? Math.round((item.questoes_corretas / item.questoes_respondidas) * 100)
                : 0,
            streak: item.streak_atual || 0,
            current_streak: item.streak_atual || 0,
            position: index + 1,
        }));

        // Só persiste no cache quando há dados reais. Cachear [] (ex.: 0 linhas
        // de uma query rodada como anon antes da sessão estar pronta) congelaria
        // o ranking vazio por 45 min e o pull-to-refresh não recuperaria.
        if (formattedData.length > 0) {
            await AsyncStorage.setItem(CACHE_KEYS.GLOBAL_RANKING, JSON.stringify(formattedData));
            await updateCacheTimestamp(cacheKey);
        }

        return formattedData;
    } catch (error) {
        captureException(error, { tags: { area: 'getGlobalRanking' } });
        return [];
    }
};

// Helpers para gerar avatares
const getAvatarEmoji = (index) => {
    const emojis = ['👩‍💻', '👨‍🎓', '👩‍🔬', '👨‍💼', '👩‍⚕️', '👨‍🏫', '🧙‍♂️', '🥷', '👩‍🎨', '👨‍🚀'];
    return emojis[index % emojis.length];
};

const getAvatarColor = (index) => {
    const colors = ['#E879F9', '#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#FFD93D', '#00D9FF', '#51CF66', '#FF6B6B'];
    return colors[index % colors.length];
};

/**
 * Busca Top 10 do ranking global
 * Cache: 40 minutos
 */
export const getGlobalTop10 = async (forceRefresh = false) => {
    const cacheKey = 'global_top10';

    if (!forceRefresh) {
        const expired = await isCacheExpired(cacheKey, CACHE_INTERVALS.GLOBAL_TOP10);
        if (!expired) {
            const cached = await AsyncStorage.getItem(CACHE_KEYS.GLOBAL_TOP10);
            if (cached) {
                const parsed = JSON.parse(cached);
                // Ignora cache vazio (envenenado por uma chamada sem sessão/RLS)
                // e força nova busca. Mesmo padrão de getGlobalRanking.
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed;
                }
            }
        }
    }

    const supabaseCache = await fetchFromSupabaseCache('global_top10');

    if (supabaseCache?.data) {
        await AsyncStorage.setItem(CACHE_KEYS.GLOBAL_TOP10, JSON.stringify(supabaseCache.data));
        await updateCacheTimestamp(cacheKey);
        return supabaseCache.data;
    }

    // Fallback: pegar do ranking global
    const fullRanking = await getGlobalRanking(forceRefresh);
    const top10 = fullRanking.slice(0, 10);

    // Só persiste no cache quando há dados reais (evita envenenar com [])
    if (top10.length > 0) {
        await AsyncStorage.setItem(CACHE_KEYS.GLOBAL_TOP10, JSON.stringify(top10));
        await updateCacheTimestamp(cacheKey);
    }

    return top10;
};

/**
 * Busca Top 3 do ranking global (Pódio)
 * Cache: 35 minutos
 */
export const getGlobalTop3 = async (forceRefresh = false) => {
    const cacheKey = 'global_top3';

    if (!forceRefresh) {
        const expired = await isCacheExpired(cacheKey, CACHE_INTERVALS.GLOBAL_TOP3);
        if (!expired) {
            const cached = await AsyncStorage.getItem(CACHE_KEYS.GLOBAL_TOP3);
            if (cached) {
                const parsed = JSON.parse(cached);
                // Ignora cache vazio (envenenado por chamada sem sessão/RLS)
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed;
                }
            }
        }
    }

    const supabaseCache = await fetchFromSupabaseCache('global_top3');

    if (supabaseCache?.data) {
        await AsyncStorage.setItem(CACHE_KEYS.GLOBAL_TOP3, JSON.stringify(supabaseCache.data));
        await updateCacheTimestamp(cacheKey);
        return supabaseCache.data;
    }

    // Fallback: pegar do ranking global
    const fullRanking = await getGlobalRanking(forceRefresh);
    const top3 = fullRanking.slice(0, 3);

    // Só persiste quando há dados (evita cache envenenado)
    if (top3.length > 0) {
        await AsyncStorage.setItem(CACHE_KEYS.GLOBAL_TOP3, JSON.stringify(top3));
        await updateCacheTimestamp(cacheKey);
    }

    return top3;
};

// ============================================
// RANKING DE DESAFIOS
// ============================================

/**
 * Busca ranking de um desafio específico
 * Cache: 35 minutos
 * ADAPTADO para schema existente do Supabase
 */
export const getChallengeRanking = async (challengeId, forceRefresh = false) => {
    const cacheKey = `challenge_${challengeId}`;

    if (!forceRefresh) {
        const expired = await isCacheExpired(cacheKey, CACHE_INTERVALS.CHALLENGE_TOP3);
        if (!expired) {
            const cached = await AsyncStorage.getItem(`${CACHE_KEYS.CHALLENGE_RANKINGS}_${challengeId}`);
            if (cached) {
                return JSON.parse(cached);
            }
        }
    }

    // Buscar do Supabase - OTIMIZADO: 1 query com JOIN implícito
    try {
        // Query única com relacionamento profiles via user_id
        const { data: participantsWithProfiles, error } = await supabase
            .from('challenge_participants')
            .select(`
                id, 
                user_id, 
                score, 
                questoes_respondidas, 
                questoes_corretas, 
                joined_at,
                profiles!challenge_participants_user_id_fkey (
                    id,
                    nome,
                    username,
                    foto_url,
                    avatar_seed,
                    nivel,
                    xp_total,
                    streak_atual
                )
            `)
            .eq('challenge_id', challengeId)
            .order('score', { ascending: false })
            .limit(100);

        if (error) {
            // Fallback: tentar query sem JOIN (caso FK não exista)
            console.warn('[rankingService] JOIN falhou, usando fallback:', error.message);
            return await getChallengeRankingFallback(challengeId);
        }

        if (!participantsWithProfiles || participantsWithProfiles.length === 0) {
            return [];
        }

        const formattedData = participantsWithProfiles.map((item, index) => {
            const profile = item.profiles || {};
            return {
                id: profile.id || item.user_id,
                user_id: item.user_id,
                username: profile.username || profile.nome || 'user',
                name: profile.nome || 'Usuário',
                display_name: profile.nome || 'Usuário',
                avatar: getAvatarEmoji(index),
                avatar_emoji: getAvatarEmoji(index),
                avatar_seed: profile.avatar_seed,
                foto_url: profile.foto_url,
                avatarBg: getAvatarColor(index),
                avatar_bg: getAvatarColor(index),
                avatarUrl: profile.foto_url,
                isOnline: Math.random() > 0.5,
                xp: item.score || 0,
                xp_earned: item.score || 0,
                questionsAnswered: item.questoes_respondidas || 0,
                questions_answered: item.questoes_respondidas || 0,
                accuracy: item.questoes_respondidas > 0
                    ? Math.round((item.questoes_corretas / item.questoes_respondidas) * 100)
                    : 0,
                level: profile.nivel || 1,
                streak: profile.streak_atual || 0,
                current_streak: profile.streak_atual || 0,
                position: index + 1,
                joinedAt: item.joined_at,
                joined_at: item.joined_at,
            };
        });

        // Salvar cache em paralelo (não bloqueia retorno)
        Promise.all([
            AsyncStorage.setItem(`${CACHE_KEYS.CHALLENGE_RANKINGS}_${challengeId}`, JSON.stringify(formattedData)),
            updateCacheTimestamp(cacheKey),
        ]).catch(() => { });

        return formattedData;
    } catch (error) {
        return [];
    }
};

/**
 * Fallback: busca ranking com 2 queries separadas (caso JOIN não funcione)
 */
const getChallengeRankingFallback = async (challengeId) => {
    const { data: participants, error: partError } = await supabase
        .from('challenge_participants')
        .select('id, user_id, score, questoes_respondidas, questoes_corretas, joined_at')
        .eq('challenge_id', challengeId)
        .order('score', { ascending: false })
        .limit(100);

    if (partError || !participants?.length) return [];

    const userIds = participants.map(p => p.user_id);
    const { data: profiles } = await supabase
        .from('profiles')
        .select('id, nome, username, foto_url, avatar_seed, nivel, xp_total, streak_atual')
        .in('id', userIds);

    const profileMap = new Map();
    profiles?.forEach(p => profileMap.set(p.id, p));

    return participants.map((item, index) => {
        const profile = profileMap.get(item.user_id) || {};
        return {
            id: profile.id || item.user_id,
            user_id: item.user_id,
            username: profile.username || profile.nome || 'user',
            name: profile.nome || 'Usuário',
            display_name: profile.nome || 'Usuário',
            avatar: getAvatarEmoji(index),
            avatar_emoji: getAvatarEmoji(index),
            avatar_seed: profile.avatar_seed,
            foto_url: profile.foto_url,
            avatarBg: getAvatarColor(index),
            avatar_bg: getAvatarColor(index),
            avatarUrl: profile.foto_url,
            isOnline: Math.random() > 0.5,
            xp: item.score || 0,
            xp_earned: item.score || 0,
            questionsAnswered: item.questoes_respondidas || 0,
            questions_answered: item.questoes_respondidas || 0,
            accuracy: item.questoes_respondidas > 0
                ? Math.round((item.questoes_corretas / item.questoes_respondidas) * 100)
                : 0,
            level: profile.nivel || 1,
            streak: profile.streak_atual || 0,
            current_streak: profile.streak_atual || 0,
            position: index + 1,
            joinedAt: item.joined_at,
            joined_at: item.joined_at,
        };
    });
};

/**
 * Busca Top 3 de um desafio (Líderes do Desafio)
 * Cache: 35 minutos
 */
export const getChallengeTop3 = async (challengeId, forceRefresh = false) => {
    const supabaseCache = await fetchFromSupabaseCache('challenge_top3', challengeId);

    if (supabaseCache?.data) {
        return supabaseCache.data;
    }

    const fullRanking = await getChallengeRanking(challengeId, forceRefresh);
    return fullRanking.slice(0, 3);
};

// ============================================
// POSIÇÃO DO USUÁRIO (TEMPO REAL)
// ============================================

/**
 * Calcula posição do usuário em tempo real
 * Baseado no XP local vs ranking em cache
 */
export const getUserPosition = async (userXp) => {
    try {
        // Buscar ranking do cache local
        const cachedRanking = await AsyncStorage.getItem(CACHE_KEYS.GLOBAL_RANKING);
        if (!cachedRanking) {
            return { position: null, estimatedPosition: null };
        }

        const ranking = JSON.parse(cachedRanking);

        // Encontrar posição baseada no XP
        let position = ranking.length + 1; // Assume última posição

        for (let i = 0; i < ranking.length; i++) {
            if (userXp >= ranking[i].xp) {
                position = i + 1;
                break;
            }
        }

        // Se usuário está no ranking, encontrar sua posição real
        const userInRanking = ranking.find(u => u.xp <= userXp);

        return {
            position,
            isEstimated: true, // É uma estimativa até próximo refresh
            totalUsers: ranking.length,
        };
    } catch (error) {

        return { position: null };
    }
};

/**
 * Busca posição real do usuário no banco
 */
export const getUserRealPosition = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('profiles')
            .select('id, xp_total')
            .order('xp_total', { ascending: false })
            .limit(1000);

        if (error) {
            return null;
        }

        const userIndex = data?.findIndex(profile => profile.id === userId) ?? -1;
        if (userIndex === -1) return null;

        const userProfile = data[userIndex];
        return {
            position: userIndex + 1,
            xp: userProfile.xp_total || 0,
            change: 0,
        };
    } catch (error) {
        return null;
    }
};

// ============================================
// DESAFIOS
// ============================================

/**
 * Busca todos os desafios ativos
 * ADAPTADO para schema existente do Supabase
 */
export const getActiveChallenges = async () => {
    try {
        const { data, error } = await supabase
            .from('challenges')
            .select('*')
            .eq('ativo', true)
            .gte('data_fim', new Date().toISOString())
            .order('tipo', { ascending: true });

        if (error) {

            return [];
        }

        // Formatar para o formato esperado pelo app - usando colunas EXISTENTES
        return data.map(challenge => {
            // Gerar cores baseadas no tipo
            const colors = getColorsForType(challenge.tipo, challenge.disciplina);

            return {
                id: challenge.id,
                type: challenge.tipo || 'daily',
                title: challenge.titulo || 'Desafio',
                subject: challenge.disciplina || challenge.assunto || 'Geral',
                description: challenge.descricao || '',
                icon: getIconForSubject(challenge.disciplina),
                color: colors.color,
                gradientColors: colors.gradient,
                prize: `${challenge.premio_xp || 100} XP`,
                prizeDescription: `${challenge.premio_xp || 100} XP`,
                prizeIcon: '\u2728',
                prizeXp: challenge.premio_xp || 100,
                startsAt: challenge.data_inicio,
                endsAt: challenge.data_fim,
                timeRemaining: getTimeRemaining(challenge.data_fim),
                isActive: challenge.ativo,
                metaQuestoes: challenge.meta_questoes || 10,
            };
        });
    } catch (error) {

        return [];
    }
};

// Helper para gerar ícone baseado na disciplina
const getIconForSubject = (disciplina) => {
    const icons = {
        'Portugu\u00eas': '\uD83D\uDCDA',
        'Matem\u00e1tica': '\uD83D\uDD22',
        'Direito': '\u2696\uFE0F',
        'Direito Constitucional': '\u2696\uFE0F',
        'Inform\u00e1tica': '\uD83D\uDCBB',
        'Racioc\u00ednio L\u00f3gico': '\uD83E\uDDE0',
        'Atualidades': '\uD83C\uDF0D',
    };
    return icons[disciplina] || '\uD83D\uDCDD';
};

// Helper para gerar cores baseadas no tipo/disciplina
const getColorsForType = (tipo, disciplina) => {
    const colorMap = {
        'Portugu\u00eas': { color: '#3B82F6', gradient: ['#3B82F6', '#1D4ED8'] },
        'Matem\u00e1tica': { color: '#8B5CF6', gradient: ['#8B5CF6', '#6D28D9'] },
        'Direito': { color: '#10B981', gradient: ['#10B981', '#059669'] },
        'Direito Constitucional': { color: '#10B981', gradient: ['#10B981', '#059669'] },
        'Inform\u00e1tica': { color: '#F59E0B', gradient: ['#F59E0B', '#D97706'] },
        'Racioc\u00ednio L\u00f3gico': { color: '#EC4899', gradient: ['#EC4899', '#DB2777'] },
        'Atualidades': { color: '#8B5CF6', gradient: ['#8B5CF6', '#7C3AED'] },
    };

    if (disciplina && colorMap[disciplina]) {
        return colorMap[disciplina];
    }

    // Cores padr\u00e3o por tipo
    if (tipo === 'weekly') {
        return { color: '#10B981', gradient: ['#10B981', '#059669'] };
    }
    return { color: '#3B82F6', gradient: ['#3B82F6', '#1D4ED8'] };
};

/**
 * Busca contagem de participantes de um desafio
 */
export const getChallengeParticipantsCount = async (challengeId) => {
    try {
        // Otimizado: contagem local em vez de HEAD
        const { data, error } = await supabase
            .from('challenge_participants')
            .select('id')
            .eq('challenge_id', challengeId)
            .limit(500); // Limite otimizado

        if (error) {

            return 0;
        }

        return data?.length || 0;
    } catch (error) {

        return 0;
    }
};

// ============================================
// UTILITÁRIOS
// ============================================

/**
 * Calcula tempo restante formatado
 */
const getTimeRemaining = (endsAt) => {
    const now = new Date();
    const end = new Date(endsAt);
    const diff = end - now;

    if (diff <= 0) return 'Encerrado';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days} dia${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
};

/**
 * Limpa todo o cache local
 */
export const clearRankingCache = async () => {
    try {
        await AsyncStorage.multiRemove([
            CACHE_KEYS.GLOBAL_RANKING,
            CACHE_KEYS.GLOBAL_TOP10,
            CACHE_KEYS.GLOBAL_TOP3,
            CACHE_KEYS.LAST_FETCH,
        ]);

    } catch (error) {

    }
};

/**
 * Obtém informações sobre o cache
 */
export const getCacheInfo = async () => {
    try {
        const lastFetchData = await AsyncStorage.getItem(CACHE_KEYS.LAST_FETCH);
        const lastFetch = lastFetchData ? JSON.parse(lastFetchData) : {};

        return {
            globalRanking: {
                lastUpdate: lastFetch.global_ranking ? new Date(lastFetch.global_ranking) : null,
                interval: CACHE_INTERVALS.GLOBAL_RANKING / 60000, // em minutos
            },
            globalTop10: {
                lastUpdate: lastFetch.global_top10 ? new Date(lastFetch.global_top10) : null,
                interval: CACHE_INTERVALS.GLOBAL_TOP10 / 60000,
            },
            globalTop3: {
                lastUpdate: lastFetch.global_top3 ? new Date(lastFetch.global_top3) : null,
                interval: CACHE_INTERVALS.GLOBAL_TOP3 / 60000,
            },
        };
    } catch (error) {

        return {};
    }
};

export default {
    getGlobalRanking,
    getGlobalTop10,
    getGlobalTop3,
    getChallengeRanking,
    getChallengeTop3,
    getUserPosition,
    getUserRealPosition,
    getActiveChallenges,
    getChallengeParticipantsCount,
    clearRankingCache,
    getCacheInfo,
};
